import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["children", "eps", "style", "className", "prepend", "center", "fullscreen", "portal", "distanceFactor", "sprite", "transform", "occlude", "onOcclude", "zIndexRange", "calculatePosition", "as", "wrapperClass", "pointerEvents"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import _extends from '@babel/runtime/helpers/esm/extends';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Vector3, OrthographicCamera, PerspectiveCamera } from 'three';
import { useThree, useFrame } from '@react-three/fiber';
var v1 = new Vector3();
var v2 = new Vector3();
var v3 = new Vector3();

function defaultCalculatePosition(el, camera, size) {
  var objectPos = v1.setFromMatrixPosition(el.matrixWorld);
  objectPos.project(camera);
  var widthHalf = size.width / 2;
  var heightHalf = size.height / 2;
  return [objectPos.x * widthHalf + widthHalf, -(objectPos.y * heightHalf) + heightHalf];
}

function isObjectBehindCamera(el, camera) {
  var objectPos = v1.setFromMatrixPosition(el.matrixWorld);
  var cameraPos = v2.setFromMatrixPosition(camera.matrixWorld);
  var deltaCamObj = objectPos.sub(cameraPos);
  var camDir = camera.getWorldDirection(v3);
  return deltaCamObj.angleTo(camDir) > Math.PI / 2;
}

function isObjectVisible(el, camera, raycaster, occlude) {
  var elPos = v1.setFromMatrixPosition(el.matrixWorld);
  var screenPos = elPos.clone();
  screenPos.project(camera);
  raycaster.setFromCamera(screenPos, camera);
  var intersects = raycaster.intersectObjects(occlude, true);

  if (intersects.length) {
    var intersectionDistance = intersects[0].distance;
    var pointDistance = elPos.distanceTo(raycaster.ray.origin);
    return pointDistance < intersectionDistance;
  }

  return true;
}

function objectScale(el, camera) {
  if (camera instanceof OrthographicCamera) {
    return camera.zoom;
  } else if (camera instanceof PerspectiveCamera) {
    var objectPos = v1.setFromMatrixPosition(el.matrixWorld);
    var cameraPos = v2.setFromMatrixPosition(camera.matrixWorld);
    var vFOV = camera.fov * Math.PI / 180;
    var dist = objectPos.distanceTo(cameraPos);
    var scaleFOV = 2 * Math.tan(vFOV / 2) * dist;
    return 1 / scaleFOV;
  } else {
    return 1;
  }
}

function objectZIndex(el, camera, zIndexRange) {
  if (camera instanceof PerspectiveCamera || camera instanceof OrthographicCamera) {
    var objectPos = v1.setFromMatrixPosition(el.matrixWorld);
    var cameraPos = v2.setFromMatrixPosition(camera.matrixWorld);
    var dist = objectPos.distanceTo(cameraPos);
    var A = (zIndexRange[1] - zIndexRange[0]) / (camera.far - camera.near);
    var B = zIndexRange[1] - A * camera.far;
    return Math.round(A * dist + B);
  }

  return undefined;
}

var epsilon = function epsilon(value) {
  return Math.abs(value) < 1e-10 ? 0 : value;
};

function getCSSMatrix(matrix, multipliers) {
  var prepend = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
  var matrix3d = 'matrix3d(';

  for (var i = 0; i !== 16; i++) {
    matrix3d += epsilon(multipliers[i] * matrix.elements[i]) + (i !== 15 ? ',' : ')');
  }

  return prepend + matrix3d;
}

var getCameraCSSMatrix = function (multipliers) {
  return function (matrix) {
    return getCSSMatrix(matrix, multipliers);
  };
}([1, -1, 1, 1, 1, -1, 1, 1, 1, -1, 1, 1, 1, -1, 1, 1]);

var getObjectCSSMatrix = function (scaleMultipliers) {
  return function (matrix, factor) {
    return getCSSMatrix(matrix, scaleMultipliers(factor), 'translate(-50%,-50%)');
  };
}(function (f) {
  return [1 / f, 1 / f, 1 / f, 1, -1 / f, -1 / f, -1 / f, -1, 1 / f, 1 / f, 1 / f, 1, 1, 1, 1, 1];
});

var Html = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      _ref$eps = _ref.eps,
      eps = _ref$eps === void 0 ? 0.001 : _ref$eps,
      style = _ref.style,
      className = _ref.className,
      prepend = _ref.prepend,
      center = _ref.center,
      fullscreen = _ref.fullscreen,
      portal = _ref.portal,
      distanceFactor = _ref.distanceFactor,
      _ref$sprite = _ref.sprite,
      sprite = _ref$sprite === void 0 ? false : _ref$sprite,
      _ref$transform = _ref.transform,
      transform = _ref$transform === void 0 ? false : _ref$transform,
      occlude = _ref.occlude,
      onOcclude = _ref.onOcclude,
      _ref$zIndexRange = _ref.zIndexRange,
      zIndexRange = _ref$zIndexRange === void 0 ? [16777271, 0] : _ref$zIndexRange,
      _ref$calculatePositio = _ref.calculatePosition,
      calculatePosition = _ref$calculatePositio === void 0 ? defaultCalculatePosition : _ref$calculatePositio,
      _ref$as = _ref.as,
      as = _ref$as === void 0 ? 'div' : _ref$as,
      wrapperClass = _ref.wrapperClass,
      _ref$pointerEvents = _ref.pointerEvents,
      pointerEvents = _ref$pointerEvents === void 0 ? 'auto' : _ref$pointerEvents,
      props = _objectWithoutProperties(_ref, _excluded);

  var _portal$current;

  var gl = useThree(function (_ref2) {
    var gl = _ref2.gl;
    return gl;
  });
  var camera = useThree(function (_ref3) {
    var camera = _ref3.camera;
    return camera;
  });
  var scene = useThree(function (_ref4) {
    var scene = _ref4.scene;
    return scene;
  });
  var size = useThree(function (_ref5) {
    var size = _ref5.size;
    return size;
  });
  var raycaster = useThree(function (_ref6) {
    var raycaster = _ref6.raycaster;
    return raycaster;
  });

  var _React$useState = React.useState(function () {
    return document.createElement(as);
  }),
      _React$useState2 = _slicedToArray(_React$useState, 1),
      el = _React$useState2[0];

  var group = React.useRef(null);
  var oldZoom = React.useRef(0);
  var oldPosition = React.useRef([0, 0]);
  var transformOuterRef = React.useRef(null);
  var transformInnerRef = React.useRef(null);
  var target = (_portal$current = portal == null ? void 0 : portal.current) !== null && _portal$current !== void 0 ? _portal$current : gl.domElement.parentNode;
  React.useEffect(function () {
    if (group.current) {
      scene.updateMatrixWorld();

      if (transform) {
        el.style.cssText = "position:absolute;top:0;left:0;pointer-events:none;overflow:hidden;";
      } else {
        var vec = calculatePosition(group.current, camera, size);
        el.style.cssText = "position:absolute;top:0;left:0;transform:translate3d(".concat(vec[0], "px,").concat(vec[1], "px,0);transform-origin:0 0;");
      }

      if (target) {
        if (prepend) target.prepend(el);else target.appendChild(el);
      }

      return function () {
        if (target) target.removeChild(el);
        ReactDOM.unmountComponentAtNode(el);
      };
    }
  }, [target, transform]);
  React.useLayoutEffect(function () {
    if (wrapperClass) el.className = wrapperClass;
  }, [wrapperClass]);
  var styles = React.useMemo(function () {
    if (transform) {
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: size.width,
        height: size.height,
        transformStyle: 'preserve-3d',
        pointerEvents: 'none'
      };
    } else {
      return _objectSpread(_objectSpread({
        position: 'absolute',
        transform: center ? 'translate3d(-50%,-50%,0)' : 'none'
      }, fullscreen && {
        top: -size.height / 2,
        left: -size.width / 2,
        width: size.width,
        height: size.height
      }), style);
    }
  }, [style, center, fullscreen, size, transform]);
  var transformInnerStyles = React.useMemo(function () {
    return {
      position: 'absolute',
      pointerEvents: pointerEvents
    };
  }, [pointerEvents]);
  React.useLayoutEffect(function () {
    if (transform) {
      ReactDOM.render( /*#__PURE__*/React.createElement("div", {
        ref: transformOuterRef,
        style: styles
      }, /*#__PURE__*/React.createElement("div", {
        ref: transformInnerRef,
        style: transformInnerStyles
      }, /*#__PURE__*/React.createElement("div", {
        ref: ref,
        className: className,
        style: style,
        children: children
      }))), el);
    } else {
      ReactDOM.render( /*#__PURE__*/React.createElement("div", {
        ref: ref,
        style: styles,
        className: className,
        children: children
      }), el);
    }
  });
  var visible = React.useRef(true);
  useFrame(function () {
    if (group.current) {
      camera.updateMatrixWorld();
      group.current.updateWorldMatrix(true, false);
      var vec = transform ? oldPosition.current : calculatePosition(group.current, camera, size);

      if (transform || Math.abs(oldZoom.current - camera.zoom) > eps || Math.abs(oldPosition.current[0] - vec[0]) > eps || Math.abs(oldPosition.current[1] - vec[1]) > eps) {
        var isBehindCamera = isObjectBehindCamera(group.current, camera);
        var raytraceTarget = false;

        if (typeof occlude === 'boolean') {
          if (occlude === true) {
            raytraceTarget = [scene];
          }
        } else if (Array.isArray(occlude)) {
          raytraceTarget = occlude.map(function (item) {
            return item.current;
          });
        }

        var previouslyVisible = visible.current;

        if (raytraceTarget) {
          var isvisible = isObjectVisible(group.current, camera, raycaster, raytraceTarget);
          visible.current = isvisible && !isBehindCamera;
        } else {
          visible.current = !isBehindCamera;
        }

        if (previouslyVisible !== visible.current) {
          if (onOcclude) onOcclude(!visible.current);else el.style.display = visible.current ? 'block' : 'none';
        }

        el.style.zIndex = "".concat(objectZIndex(group.current, camera, zIndexRange));

        if (transform) {
          var widthHalf = size.width / 2,
              heightHalf = size.height / 2;
          var fov = camera.projectionMatrix.elements[5] * heightHalf;
          var isOrthographicCamera = camera.isOrthographicCamera,
              top = camera.top,
              left = camera.left,
              bottom = camera.bottom,
              right = camera.right;
          var cameraMatrix = getCameraCSSMatrix(camera.matrixWorldInverse);
          var cameraTransform = isOrthographicCamera ? "scale(".concat(fov, ")translate(").concat(epsilon(-(right + left) / 2), "px,").concat(epsilon((top + bottom) / 2), "px)") : "translateZ(".concat(fov, "px)");
          var matrix = group.current.matrixWorld;

          if (sprite) {
            matrix = camera.matrixWorldInverse.clone().transpose().copyPosition(matrix).scale(group.current.scale);
            matrix.elements[3] = matrix.elements[7] = matrix.elements[11] = 0;
            matrix.elements[15] = 1;
          }

          el.style.width = size.width + 'px';
          el.style.height = size.height + 'px';
          el.style.perspective = isOrthographicCamera ? '' : "".concat(fov, "px");

          if (transformOuterRef.current && transformInnerRef.current) {
            transformOuterRef.current.style.transform = "".concat(cameraTransform).concat(cameraMatrix, "translate(").concat(widthHalf, "px,").concat(heightHalf, "px)");
            transformInnerRef.current.style.transform = getObjectCSSMatrix(matrix, 1 / ((distanceFactor || 10) / 400));
          }
        } else {
          var scale = distanceFactor === undefined ? 1 : objectScale(group.current, camera) * distanceFactor;
          el.style.transform = "translate3d(".concat(vec[0], "px,").concat(vec[1], "px,0) scale(").concat(scale, ")");
        }

        oldPosition.current = vec;
        oldZoom.current = camera.zoom;
      }
    }
  });
  return /*#__PURE__*/React.createElement("group", _extends({}, props, {
    ref: group
  }));
});
export { Html };